/**
 * @typedef {Object} CustomVoiceOption
 * @property {string} value
 * @property {string} [label]
 * @property {Record<string, string>} [dataset]
 * @property {(optionEl: HTMLElement) => HTMLElement | DocumentFragment | string | void} [renderOption]
 * @property {(optionEl: HTMLElement) => HTMLElement | DocumentFragment | string | void} [renderSelected]
 */

/**
 * @param {Object} config
 * @param {string} [config.id]
 * @param {string} [config.className]
 * @param {string} [config.ariaLabel]
 * @param {string} [config.placeholder]
 * @param {string} [config.closeScopeSelector] - When set, only close sibling selects inside this ancestor
 * @param {CustomVoiceOption[]} config.options
 * @param {boolean} [config.searchable]
 * @param {string} [config.searchPlaceholder]
 * @param {(query: string, baseOptions: CustomVoiceOption[]) => CustomVoiceOption[]} [config.getSearchOptions]
 * @param {(query: string, baseOptions: CustomVoiceOption[]) => CustomVoiceOption | null} [config.commitSearchQuery]
 * @param {(value: string, optionEl: HTMLElement) => void} [config.onSelect]
 * @param {() => void} [config.onBeforeOpen]
 */
export const createCustomVoiceSelect = ({
  id,
  className = 'custom-voice-select',
  ariaLabel,
  placeholder = '',
  closeScopeSelector = null,
  options = [],
  searchable = false,
  searchPlaceholder = '',
  getSearchOptions = null,
  commitSearchQuery = null,
  onSelect,
  onBeforeOpen,
}) => {
  const container = document.createElement('div');
  container.className = className;
  if (id) container.id = id;

  const displayButton = document.createElement('button');
  displayButton.type = 'button';
  displayButton.className = 'custom-voice-display';
  displayButton.setAttribute('aria-haspopup', 'listbox');
  displayButton.setAttribute('aria-expanded', 'false');
  if (ariaLabel) displayButton.setAttribute('aria-label', ariaLabel);

  const displayText = document.createElement('span');
  displayText.className = 'voice-display-text';
  displayText.textContent = placeholder;

  const arrow = document.createElement('span');
  arrow.className = 'voice-display-arrow';
  arrow.textContent = '▼';

  displayButton.appendChild(displayText);
  displayButton.appendChild(arrow);

  const dropdown = document.createElement('div');
  dropdown.className = 'custom-voice-dropdown';
  dropdown.setAttribute('role', 'listbox');

  /** @type {Record<string, HTMLElement>} */
  let optionsByValue = {};
  /** @type {Record<string, CustomVoiceOption>} */
  let optionDefsByValue = {};
  let baseOptions = options.slice();

  const searchInput = searchable ? document.createElement('input') : null;
  if (searchInput) {
    searchInput.type = 'text';
    searchInput.className = 'custom-voice-search';
    searchInput.autocomplete = 'off';
    searchInput.spellcheck = false;
    searchInput.placeholder = searchPlaceholder;
    searchInput.setAttribute('aria-label', searchPlaceholder || 'Search voices');
  }

  const optionsList = document.createElement('div');
  optionsList.className = 'custom-voice-options';

  const appendRendered = (target, rendered) => {
    if (rendered instanceof Node) {
      target.appendChild(rendered);
      return;
    }
    if (typeof rendered === 'string') {
      target.innerHTML = rendered;
    }
  };

  const buildOptionEl = (opt) => {
    const optionEl = document.createElement('div');
    optionEl.className = 'custom-voice-option';
    optionEl.dataset.value = opt.value;
    if (opt.dataset) {
      Object.entries(opt.dataset).forEach(([key, val]) => {
        optionEl.dataset[key] = val;
      });
    }
    optionEl.setAttribute('role', 'option');
    optionEl.setAttribute('tabindex', '0');

    const rendered = opt.renderOption?.(optionEl);
    if (rendered instanceof Node) {
      optionEl.appendChild(rendered);
    } else if (typeof rendered === 'string') {
      optionEl.innerHTML = rendered;
    } else if (opt.label !== undefined) {
      optionEl.innerHTML = `<span class="voice-name">${opt.label}</span>`;
    }

    return optionEl;
  };

  const rebuildOptions = (nextOptions = []) => {
    optionsList.innerHTML = '';
    optionsByValue = {};
    optionDefsByValue = Object.fromEntries(nextOptions.map((opt) => [opt.value, opt]));

    nextOptions.forEach((opt) => {
      const optionEl = buildOptionEl(opt);
      optionsList.appendChild(optionEl);
      optionsByValue[opt.value] = optionEl;
    });
  };

  const renderOptions = (query = '') => {
    const nextOptions = typeof getSearchOptions === 'function'
      ? getSearchOptions(query, baseOptions)
      : baseOptions;
    rebuildOptions(nextOptions);
  };

  renderOptions('');

  if (searchInput) dropdown.appendChild(searchInput);
  dropdown.appendChild(optionsList);

  container.appendChild(displayButton);
  container.appendChild(dropdown);

  const updateDisplay = (value, optionEl) => {
    const optDef = optionDefsByValue[value];
    displayText.innerHTML = '';

    const rendered = optDef?.renderSelected?.(optionEl);
    if (rendered !== undefined) {
      appendRendered(displayText, rendered);
      return;
    }

    if (optDef?.label !== undefined) {
      displayText.textContent = optDef.label;
      return;
    }

    displayText.textContent = placeholder;
  };

  const applySelection = (value, { silent = false } = {}) => {
    const optionEl = optionsByValue[value];
    if (!optionEl) return;

    Object.values(optionsByValue).forEach((el) => el.classList.remove('selected'));
    optionEl.classList.add('selected');
    container.dataset.value = value;
    container.dataset.customVoice = optionEl.dataset.customVoice === 'true' ? 'true' : '';
    updateDisplay(value, optionEl);

    if (optionEl.dataset.customVoice !== 'true') {
      baseOptions = baseOptions.filter((item) => item.dataset?.customVoice !== 'true');
    }

    if (!silent) onSelect?.(value, optionEl);
  };

  const selectUnlistedOption = (opt, { silent = true } = {}) => {
    baseOptions = baseOptions.filter((item) => item.value !== opt.value && item.dataset?.customVoice !== 'true');
    baseOptions.push(opt);
    rebuildOptions(baseOptions);
    applySelection(opt.value, { silent });
  };

  const closeDropdown = ({ commitSearch = true } = {}) => {
    const query = commitSearch && searchInput ? searchInput.value.trim() : '';
    if (searchInput) searchInput.value = '';
    if (query && typeof commitSearchQuery === 'function') {
      const opt = commitSearchQuery(query, baseOptions);
      if (opt?.value) selectUnlistedOption(opt, { silent: false });
    }
    container.classList.remove('active');
    displayButton.classList.remove('open');
    displayButton.setAttribute('aria-expanded', 'false');
    if (searchInput) renderOptions('');
  };

  const openDropdown = () => {
    onBeforeOpen?.();

    const scopeRoot = closeScopeSelector
      ? container.closest(closeScopeSelector)
      : null;
    const activeSelector = scopeRoot
      ? scopeRoot.querySelectorAll('.custom-voice-select.active, .character-voice-language-control.active')
      : document.querySelectorAll('.custom-voice-select.active, .character-voice-language-control.active');

    activeSelector.forEach((el) => {
      if (el !== container && typeof el.close === 'function') el.close();
    });

    container.classList.add('active');
    displayButton.classList.add('open');
    displayButton.setAttribute('aria-expanded', 'true');
    if (searchInput) {
      renderOptions(searchInput.value);
      searchInput.focus();
    }
  };

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (container.classList.contains('active')) {
      closeDropdown();
      return;
    }

    openDropdown();
  };

  const handleOptionSelect = (optionEl) => {
    applySelection(optionEl.dataset.value ?? '');
    closeDropdown({ commitSearch: false });
  };

  displayButton.addEventListener('click', handleToggle);
  displayButton.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown({ commitSearch: false });
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') handleToggle(e);
  });

  optionsList.addEventListener('click', (e) => {
    const optionEl = e.target instanceof Element ? e.target.closest('.custom-voice-option') : null;
    if (!optionEl) return;
    handleOptionSelect(optionEl);
  });

  if (searchInput) {
    searchInput.addEventListener('click', (event) => event.stopPropagation());
    searchInput.addEventListener('input', () => renderOptions(searchInput.value));
    searchInput.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown({ commitSearch: false });
        return;
      }
      if (event.key !== 'Enter') return;
      event.preventDefault();
      const customOption = optionsList.querySelector('[data-custom-voice="true"]');
      if (customOption instanceof HTMLElement) handleOptionSelect(customOption);
    });
  }

  dropdown.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDropdown({ commitSearch: false });
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const optionEl = e.target instanceof Element ? e.target.closest('.custom-voice-option') : null;
    if (!optionEl) return;
    e.preventDefault();
    handleOptionSelect(optionEl);
  });

  document.addEventListener('click', (e) => {
    if (e.target instanceof Node && !container.contains(e.target)) closeDropdown();
  });

  container.getValue = () => container.dataset.value ?? '';
  container.setValue = (value) => applySelection(value);
  container._setValue = (value) => applySelection(value, { silent: true });
  container._setOptions = (nextOptions, selectedValue = null) => {
    const previousValue = selectedValue ?? container.dataset.value ?? '';
    baseOptions = nextOptions.slice();
    renderOptions(searchInput?.value || '');
    const visible = typeof getSearchOptions === 'function'
      ? getSearchOptions(searchInput?.value || '', baseOptions)
      : baseOptions;
    const fallback = visible.some((opt) => opt.value === previousValue)
      ? previousValue
      : (visible[0]?.value ?? '');
    if (fallback) applySelection(fallback, { silent: true });
  };
  container.selectUnlisted = selectUnlistedOption;
  container.close = closeDropdown;

  return container;
};
